import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { DataGrid } from '@tableau/widgets-datagrid';
import { MetricValuePill } from '@tableau/metrics-shared-widgets-dom';

import { CommonStyles } from '../../common/react/CommonStyles';
import { getMeasureFormat, getMetricMeasure, getTableWithDefaultComparison } from '..';
import { getColorAccessor, getMeasureAccessor, selectMetricDataForContentItem } from '../selectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IMetricDataInfo, isMetricInfo } from '../models/IMetricInfo';
import { IExplorableItemTypeInfo } from '../../ts/models/resourceInfoParts';
import { AppState } from '../../app/state/AppState';
import { MapStateToProps } from '../../app/utils/ConnectUtils';
import { computeMetricComparison, DataRow, getLatestValue, MetricBlue, MetricsNeutralColor } from '@tableau/metrics-engine';

const containerCellStyle: React.CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'row',
  paddingTop: CommonStyles.Sizes.MediumSpace,
  alignItems: 'center',
  justifyContent: 'center',
  width: CommonStyles.Sizes.MetricValueCellWidth,
  height: CommonStyles.Sizes.RowHeight,
};

// we have a connected metric value pill so that as metric data comes in and updates, we only re-render the pill,
// not the whole data grid. This is tested in test\src\ts\app\spaces\ListView\DataGridCellsTests.tsx
export interface MetricValuePillWrapperStateProps {
  readonly metricData: IMetricDataInfo;
  readonly measureAccessor: (point: DataRow) => number;
  readonly colorAccessor: (point: DataRow) => string;
}

export interface MetricValuePillWrapperOwnProps {
  readonly row: DataGrid.Row;
}

export type MetricValuePillWrapperProps = MetricValuePillWrapperStateProps & MetricValuePillWrapperOwnProps;

export class MetricValuePillWrapper extends React.PureComponent<MetricValuePillWrapperProps> {
  public render() {
    const rowData = this.props.row.data as IExplorableItemTypeInfo;
    if (!isMetricInfo(rowData) || !FeatureFlags.metricsSpaceEnabled) {
      // render blank cell for non-metrics. This happens in mixed content pages.
      return null;
    }

    let measure;
    let format;

    if (this.props.metricData && this.props.metricData.dataPoints && this.props.metricData.dataPoints.length > 0) {
      measure = getMetricMeasure(this.props.metricData, this.props.measureAccessor);
      format = getMeasureFormat(this.props.metricData);
    }

    const comparisonTable = this.props.metricData && getTableWithDefaultComparison(this.props.metricData);

    const latestValue = comparisonTable && getLatestValue(comparisonTable);
    const comparison = latestValue && computeMetricComparison(latestValue);

    const colorsEnabledColor =
      latestValue && comparison
        ? this.props.colorAccessor(comparison.comparedPoint)
        : MetricsNeutralColor;

    const color = FeatureFlags.metricColorIndicatorsEnabled ? colorsEnabledColor : MetricBlue;

    return (
      <span style={containerCellStyle}>
        <MetricValuePill value={measure} measureFormat={format} color={color} />
      </span>
    );
  }
}

const mapStateToProps: MapStateToProps<MetricValuePillWrapperStateProps> = (state: AppState, ownProps: MetricValuePillWrapperOwnProps) =>({
  metricData: ownProps.row.data ? selectMetricDataForContentItem(state, ownProps.row.data) : {},
  measureAccessor: getMeasureAccessor(state),
  colorAccessor: getColorAccessor(state)
});

export const ConnectedMetricValuePillWrapper = connect(mapStateToProps)(MetricValuePillWrapper);
